import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const NewsContainer = () => {
  const [newsList, setNewsList] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();
        const newsRef = collection(db, "news");
        const snapshot = await getDocs(newsRef);

        if (snapshot.empty) {
          console.warn("Haber bulunamadı.");
          setNewsList([]);
          return;
        }

        const allNewsData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            let imageUrl = "/images/placeholder.jpg";

            if (data.image_url) {
              try {
                const imageRef = ref(storage, data.image_url);
                imageUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.warn("Görsel yüklenemedi:", error.message);
              }
            }

            let formattedDate = "Tarih belirtilmemiş";
            if (data.timestamp) {
              try {
                const dateObj = data.timestamp.toDate();
                formattedDate = dateObj.toLocaleDateString("tr-TR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                });
              } catch (error) {
                console.warn("Tarih dönüştürme hatası:", error.message);
              }
            }

            // Document ID'yi numeric olarak çıkar
            const docId = doc.id;
            const numericId = parseInt(docId, 10);
            
            return {
              id: docId,
              numericId: isNaN(numericId) ? 0 : numericId,
              title: data.title || "Başlıksız Haber",
              content: data.content || "İçerik bulunamadı.",
              date: formattedDate,
              imageUrl,
              timestamp: data.timestamp || null
            };
          })
        );

        // En yüksek numeric ID'ye sahip son 8 haberi al
        const sortedNews = allNewsData.sort((a, b) => b.numericId - a.numericId);
        const last8News = sortedNews.slice(0, 8);
        
        setNewsList(last8News);
      } catch (err) {
        console.error("Haberler alınırken hata oluştu:", err);
        setError("Haberler yüklenirken bir hata oluştu.");
      }
    };

    fetchNews();
  }, []);

  const handleCardClick = (id) => {
    navigate(`/haber/${id}`);
  };

  const handleViewAllNews = () => {
    navigate("/haberler");
  };

  if (error) {
    return (
      <div className="news-error">
        <p>⚠️ {error}</p>
        <button onClick={() => window.location.reload()}>Tekrar Dene</button>
      </div>
    );
  }

  if (newsList.length === 0) {
    return (
      <div className="news-empty">
        <p>Henüz haber eklenmemiş.</p>
      </div>
    );
  }

  return (
    <div className="news-section">
      <h2 className="news-header">Son Haberler</h2>
      <div className="news-grid">
        {newsList.map((item) => (
          <div
            key={item.id}
            className="news-card"
            onClick={() => handleCardClick(item.id)}
          >
            <div className="news-image-container">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="news-image"
                onError={(e) => {
                  e.target.src = "/images/placeholder.jpg";
                }}
              />
            </div>
            <div className="news-content">
              <h3 className="news-title">{item.title}</h3>
              <div className="news-description">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {item.content.length > 130
                    ? item.content.slice(0, 130) + "..."
                    : item.content}
                </ReactMarkdown>
              </div>
              <div className="news-meta">
                <span>Tarih: {item.date}</span>
              </div>
              <button
                className="news-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(item.id);
                }}
              >
                Haberi Oku
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Tüm Haberler Butonu */}
      <div className="view-all-container">
        <button className="view-all-button" onClick={handleViewAllNews}>
          Tüm Haberler
        </button>
      </div>

      {/* --- STYLES --- */}
      <style>{`
        .news-section { padding: 60px 20px; background-color: #f9fafc; font-family: "Inter", sans-serif; }
        .news-header { text-align: center; font-size: 2rem; font-weight: 700; margin-bottom: 40px; color: #1a1a1a; }
        .news-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 25px; max-width: 1200px; margin: 0 auto; }
        .news-card { background-color: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); transition: all 0.3s ease; cursor: pointer; display: flex; flex-direction: column; }
        .news-card:hover { transform: translateY(-5px); box-shadow: 0 8px 18px rgba(0,0,0,0.15); }
        .news-image-container { height: 200px; background: #f5f5f5; }
        .news-image { width: 100%; height: 100%; object-fit: cover; }
        .news-content { padding: 18px 20px 24px; flex: 1; display: flex; flex-direction: column; }
        .news-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 10px; color: #333; }
        .news-description { font-size: 0.9rem; color: #666; line-height: 1.5; margin-bottom: 15px; flex-grow: 1; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .news-meta { font-size: 0.85rem; color: #777; margin-bottom: 12px; }
        .news-button { padding: 12px; background-color: #ffc107; color: #333; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background 0.3s ease; }
        .news-button:hover { background-color: #e0a800; }
        
        .view-all-container { display: flex; justify-content: center; margin-top: 40px; }
        .view-all-button { padding: 14px 36px; background-color: #001E78; color: white; border: none; border-radius: 6px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 4px 8px rgba(74, 111, 165, 0.2); }
        .view-all-button:hover { background-color: #3a5a8c; transform: translateY(-2px); box-shadow: 0 6px 12px rgba(74, 111, 165, 0.3); }
        .view-all-button:active { transform: translateY(0); }
        
        .news-error, .news-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; text-align: center; font-size: 1rem; color: #555; }
        .news-error button { margin-top: 15px; padding: 10px 20px; background-color: #ff5722; color: white; border: none; border-radius: 4px; cursor: pointer; }

        @media (max-width: 768px) {
          .news-header { font-size: 1.6rem; }
          .news-content { padding: 16px; }
          .news-title { font-size: 1rem; }
          .news-description { font-size: 0.85rem; }
          .news-meta { font-size: 0.8rem; }
          .view-all-button { padding: 12px 28px; font-size: 0.95rem; }
        }

        @media (max-width: 480px) {
          .news-header { font-size: 1.4rem; margin-bottom: 25px; }
          .news-grid { gap: 20px; }
          .news-card { border-radius: 10px; }
          .news-button { padding: 10px; font-size: 0.9rem; }
          .view-all-button { padding: 10px 24px; font-size: 0.9rem; }
        }
      `}</style>
    </div>
  );
};

export default NewsContainer;